import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { SnFormRef } from "@widgets/SnForm";
import ComCreateUi from "@menu/COM/ComCreateUi";
import layout from "./SN_PRD_RUN_REG_001_CHEM_POP_002.json";
import { showDialog } from "@widgets/CommonDialog";
import { ValueChangedEvent } from "@shared/type/CommonType";
import { UseApiService } from "@shared/hooks/useApiService";
import { fnGetwhLocLotItemScan } from "@shared/utils/InvUtils";
import * as converter from "@shared/utils/ValueConverterUtils";

interface popupProps {
  recipe: any;
  cdWh: any;
  barcode: any;
  noLotCust: any;
  qtInv: any;
  lotList: any[];
  recentEquip: any;
}
const SN_PRD_RUN_REG_001_CHEM_POP_002 = forwardRef(
  (
    {
      recipe,
      cdWh,
      barcode,
      noLotCust,
      qtInv,
      lotList,
      recentEquip,
    }: popupProps,
    popupRef: React.Ref<any>
  ) => {
    const { userInfo } = global();
    const { apiPost } = UseApiService();

    const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      freeform1: React.createRef(),
    });
    const [formControls, setFormControls] = useState<any>({
      freeform1: [],
    });
    const [formDatas, setFormDatas] = useState<any>({
      freeform1: null,
    });

    const getFormCurrent = (key: string): SnFormRef => {
      const form = formRefs.current[key]?.current;
      if (!form) {
        throw new Error(`form '${key}' is not mounted yet`);
      }
      return form;
    };

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_PRD_RUN_REG_001_CHEM_POP_002"
          );
          setFormControls({
            freeform1: rtn.form.freeform1,
          });
          setFormDatas({
            freeform1: recipe,
          });

          if (barcode !== null) {
            setFormDatas((prev: any) => ({
              ...prev,
              freeform1: {
                ...prev.freeform1,
                barcode: barcode,
                no_lot_cust: noLotCust,
                qt: 0,
                qt_inv: qtInv,
                qt_remain: Math.max(0, recipe.qt_matl_adjust - recipe.qt_use),
                cd_equip: recentEquip,
              },
            }));

            getFormCurrent("freeform1").setFocus("cd_equip");
          }
        } catch (error) {}
      };
      fetchform();
    }, []);

    useImperativeHandle(popupRef, () => ({
      checkValidity: async () => {
        const data = formDatas.freeform1;
        const qt = Number(data.qt);

        if (converter.getString(data.barcode) === "") {
          showDialog("alter", "LOT정보를 스캔해 주세요.");
          return false;
        }

        if (qt === 0) {
          await showDialog("alter", "투입량이 0입니다.");
          return false;
        } else if (qt > data.qt_inv) {
          await showDialog("alter", "재고량보다 투입량이 많습니다.");
          return false;
        }

        // 기투입량 수정
        recipe.qt_use += qt;
        return true;
      },
      getOkayData: () => {
        const data = formDatas.freeform1;

        const lotData: any[] = [];
        const resultData = {
          cd_matl: data.cd_matl,
          qt_use: Number(data.qt),
          no_lot: data.barcode,
          no_lot_cust: data.no_lot_cust,
          cd_equip: data.cd_equip,
        };
        lotData.push(resultData);
        return {
          rtnData: lotData,
        };
      },
    }));

    const onEnter = async (sender: string, value: string) => {
      let hasValid = false;
      lotList.forEach((item) => {
        if (item.no_lot === value) {
          hasValid = true;
        }
      });

      if (hasValid) {
        await showDialog("alter", "이미 투입된 LOT입니다.");
        // 포커스 유지
        getFormCurrent("freeform1").setFocus("no_barcode");
        return;
      }

      const param = {
        cd_biz: userInfo.cd_biz,
        cd_wh: cdWh,
        no_barcode: value,
        cd_loc: "",
        cd_item: recipe.cd_matl,
      };

      const invData = await fnGetwhLocLotItemScan(param);

      if (invData.length < 1) {
        await showDialog(
          "alter",
          "품목과 일치하지 않는 LOT정보이거나 LOT정보를 찾을 수 없습니다."
        );

        // 포커스 유지
        getFormCurrent("freeform1").setFocus("no_barcode");
      } else if (invData[0].qt_inv <= 0) {
        await showDialog("alter", "재고가 0인 LOT정보 입니다.");

        // 포커스 유지
        getFormCurrent("freeform1").setFocus("no_barcode");
      } else {
        setFormDatas((prev: any) => ({
          ...prev,
          freeform1: {
            ...prev.freeform1,
            qt_inv: invData[0].qt_inv,
            qt: 0,
            barcode: value,
            no_lot_cust: invData[0].no_lot_cust,
            cd_matl: recipe.cd_matl,
            nm_matl: recipe.nm_matl,
            dc_item_spec: recipe.dc_item_spec,
            dc_item_unit: recipe.dc_item_unit,
            qt_matl_adjust: recipe.qt_matl_adjust,
            qt_inf: recipe.qt_inf,
            qt_sup: recipe.qt_sup,
            qt_use: recipe.qt_use,
            qt_remain: Math.max(0, recipe.qt_matl_adjust - recipe.qt_use),
          },
        }));
      }
    };

    const onBtnEtcClick = async (id: string) => {
      try {
        switch (id) {
          case "btnEquip":
            const formData = getFormCurrent("freeform1").getFormdata();

            const data = formDatas.freeform1;
            if (converter.getString(data.barcode) === "") {
              await showDialog("alter", "투입 LOT가 선택되지 않았습니다.");
              return;
            }

            if (converter.getString(formData.cd_equip) === "") {
              await showDialog("alter", "설비를 선택해주세요.");
              return;
            }

            const rtn = await apiPost("/SN_PRD_RUN_REG_001_CHEM_READ", {
              equip: formData.cd_equip,
              grpEquip: "200",
            });
            let value = Math.abs(rtn.data?.qt);
            if (value == 0) value = 0;
            else
              value =
                formData.cd_equip === "W001" || formData.cd_equip === "W004"
                  ? value / 100
                  : value;
            setFormDatas((prev: any) => ({
              ...prev,
              freeform1: {
                ...prev.freeform1,
                qt: value,
              },
            }));
            break;
          case "btnClearEquip":
            setFormDatas((prev: any) => ({
              ...prev,
              freeform1: {
                ...prev.freeform1,
                cd_equip: "",
              },
            }));
            getFormCurrent("freeform1").setFocus("cd_equip");
            break;
        }
      } catch (error) {}
    };

    const onValueChanged = (event: ValueChangedEvent) => {
      switch (event.colId) {
        case "qt":
          setFormDatas((prev: any) => ({
            ...prev,
            freeform1: {
              ...prev.freeform1,
              qt: event.newValue,
            },
          }));
          break;
        case "cd_equip":
          setFormDatas((prev: any) => ({
            ...prev,
            freeform1: {
              ...prev.freeform1,
              cd_equip: event.newValue,
            },
          }));
          break;
      }
    };

    const onNumPadClick = (key: string) => {
      const formData = getFormCurrent("freeform1").getFormdata();
      let calcQt = String(formData.qt);

      switch (key) {
        case "C":
          calcQt = "0";
          break;
        case "B":
          calcQt = calcQt.slice(0, -1) || "0";

          if (calcQt.endsWith(".")) {
            calcQt = calcQt.slice(0, -1) || "0";
          }
          break;
        case "D":
          if (!calcQt.includes(".")) {
            calcQt = calcQt === "0" ? "0." : calcQt + ".";
          }
          break;
        default:
          calcQt = calcQt === "0" ? key : calcQt + key;
          break;
      }

      if (calcQt.includes(".")) {
        const [i, d = ""] = calcQt.split(".");
        calcQt = `${i}.${d.slice(0, 2)}`;
      }

      setFormDatas((prev: any) => ({
        ...prev,
        freeform1: {
          ...prev.freeform1,
          qt: calcQt,
        },
      }));
    };

    const forms = useMemo(
      () => ({
        freeform1: {
          controls: formControls.freeform1,
          data: formDatas.freeform1,
          popupRef: popupRef,
          events: {
            onEnter,
            onBtnEtcClick,
            onValueChanged,
          },
        },
      }),
      [formControls, formDatas]
    );

    const buttons = useMemo(
      () => ({
        btn1: { onClick: () => onNumPadClick("1") },
        btn2: { onClick: () => onNumPadClick("2") },
        btn3: { onClick: () => onNumPadClick("3") },
        btn4: { onClick: () => onNumPadClick("4") },
        btn5: { onClick: () => onNumPadClick("5") },
        btn6: { onClick: () => onNumPadClick("6") },
        btn7: { onClick: () => onNumPadClick("7") },
        btn8: { onClick: () => onNumPadClick("8") },
        btn9: { onClick: () => onNumPadClick("9") },
        btn0: { onClick: () => onNumPadClick("0") },
        btnDot: { onClick: () => onNumPadClick("D") },
        btnBack: { onClick: () => onNumPadClick("B") },
        btnClear: { onClick: () => onNumPadClick("C") },
      }),
      []
    );

    const dynamicProps = React.useMemo(
      () => ({
        forms,
        formRefs,
        buttons,
      }),
      [formRefs, forms, popupRef]
    );

    //region UI
    return <ComCreateUi dynamicProps={dynamicProps} layoutConfig={layout} />;
  }
);

export default SN_PRD_RUN_REG_001_CHEM_POP_002;
